import React from 'react';
import { Lock, Unlock, FolderOpen, Folder, Clock } from 'lucide-react';
import { colors } from '../../theme/colors';

const CaseStatusContent = ({
  caseStatus,
  selectedStatus,
  setSelectedStatus,
  statusComment,
  setStatusComment,
  isUpdatingStatus,
  setIsUpdatingStatus,
  setCaseStatus,
  setShowCaseStatusPanel,
  location,
  dispatch,
  addNotification
}) => {
  const statusOptions = [
    {
      key: 'open',
      label: 'Open',
      icon: FolderOpen,
      color: colors.primary, // Use theme primary color
      description: 'Case is active and being worked on'
    },
    {
      key: 'under_review',
      label: 'Under Review',
      icon: Clock,
      color: '#f59e0b', // Orange
      description: 'Case is being reviewed for completion'
    },
    {
      key: 'closed',
      label: 'Closed',
      icon: Folder,
      color: '#6b7280', // Gray
      description: 'Case has been completed and closed'
    }
  ];

  const handleStatusSave = async () => {
    setIsUpdatingStatus(true);
    try {
      // Get current loan ID from URL params
      const loanId = location.pathname.split('/').pop();
      
      // Here you would make an API call to update the case status
      // For now, we'll just update the local state
      setCaseStatus(selectedStatus);
      
      // Show success notification
      dispatch(addNotification({
        type: 'success',
        message: `Case status updated to ${statusOptions.find(s => s.key === selectedStatus)?.label}${statusComment ? ' with comment' : ''}`
      }));
      
      // Reset form and close panel
      setStatusComment('');
      setShowCaseStatusPanel(false);
      
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to update case status'
      }));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleStatusCancel = () => {
    setSelectedStatus(caseStatus); // Reset to current status
    setStatusComment('');
    setShowCaseStatusPanel(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ 
        padding: '1rem', 
        background: '#3a3a3a', 
        borderRadius: '6px',
        border: '1px solid #4a4a4a'
      }}>
        <h3 style={{ 
          color: '#e0e0e0', 
          margin: '0 0 0.5rem 0', 
          fontSize: '1rem',
          fontWeight: '600'
        }}>
          Current Status
        </h3>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          padding: '0.75rem',
          background: '#2a2a2a',
          borderRadius: '4px',
          border: '1px solid #4a4a4a'
        }}>
          {(() => {
            const currentStatus = statusOptions.find(s => s.key === caseStatus);
            const StatusIcon = currentStatus?.icon || FolderOpen;
            return (
              <>
                <StatusIcon 
                  size={16} 
                  color={currentStatus?.color || '#3b82f6'}
                  style={{ flexShrink: 0 }}
                />
                <span style={{ 
                  color: '#e0e0e0', 
                  fontWeight: '500',
                  fontSize: '0.875rem'
                }}>
                  {currentStatus?.label || 'Open'}
                </span>
              </>
            );
          })()}
        </div>
      </div>

      <div style={{ 
        padding: '1rem', 
        background: '#3a3a3a', 
        borderRadius: '6px',
        border: '1px solid #4a4a4a'
      }}>
        <h3 style={{ 
          color: '#e0e0e0', 
          margin: '0 0 1rem 0', 
          fontSize: '1rem',
          fontWeight: '600'
        }}>
          Update Status
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {statusOptions.map((status) => {
            const isSelected = selectedStatus === status.key;
            
            return (
              <button
                key={status.key}
                onClick={() => setSelectedStatus(status.key)}
                disabled={isUpdatingStatus}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '0.875rem',
                  background: isSelected ? status.color : '#2a2a2a',
                  border: `1px solid ${isSelected ? status.color : '#4a4a4a'}`,
                  borderRadius: '4px',
                  color: isSelected ? 'white' : '#e0e0e0',
                  cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: isUpdatingStatus ? 0.7 : 1,
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                <status.icon 
                  size={16} 
                  color={isSelected ? '#ffffff' : status.color}
                  style={{ flexShrink: 0 }}
                />
                <div style={{ 
                  flex: 1, 
                  background: 'transparent',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderColor: 'transparent',
                  boxShadow: 'none',
                  outline: 'none'
                }}>
                  <div style={{ 
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    marginBottom: '0.25rem',
                    color: isSelected ? 'white' : '#e0e0e0',
                    background: 'transparent',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderColor: 'transparent',
                    boxShadow: 'none',
                    outline: 'none',
                    padding: '0',
                    margin: '0 0 0.25rem 0'
                  }}>
                    {status.label}
                  </div>
                  <div style={{ 
                    fontSize: '0.75rem',
                    opacity: 0.8,
                    color: isSelected ? 'white' : '#e0e0e0',
                    background: 'transparent',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderColor: 'transparent',
                    boxShadow: 'none',
                    outline: 'none',
                    padding: '0',
                    margin: '0'
                  }}>
                    {status.description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Comment Section */}
      <div style={{ 
        padding: '1rem', 
        background: '#3a3a3a', 
        borderRadius: '6px',
        border: '1px solid #4a4a4a'
      }}>
        <h3 style={{ 
          color: '#e0e0e0', 
          margin: '0 0 0.75rem 0', 
          fontSize: '1rem',
          fontWeight: '600'
        }}>
          Add Comment (Optional)
        </h3>
        <textarea
          value={statusComment}
          onChange={(e) => setStatusComment(e.target.value)}
          placeholder="Add a comment about this status change..."
          disabled={isUpdatingStatus}
          style={{
            width: '100%',
            minHeight: '80px',
            padding: '0.75rem',
            background: '#2a2a2a',
            border: '1px solid #4a4a4a',
            borderRadius: '4px',
            color: '#e0e0e0',
            fontSize: '0.875rem',
            resize: 'vertical',
            outline: 'none',
            fontFamily: 'inherit'
          }}
        />
      </div>

      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '0.75rem',
        marginTop: '1rem'
      }}>
        <button
          onClick={handleStatusCancel}
          disabled={isUpdatingStatus}
          style={{
            flex: 1,
            padding: '0.75rem 1rem',
            background: '#4a4a4a',
            border: '1px solid #6a6a6a',
            borderRadius: '4px',
            color: '#e0e0e0',
            cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500',
            transition: 'all 0.2s',
            opacity: isUpdatingStatus ? 0.7 : 1
          }}
          onMouseEnter={(e) => {
            if (!isUpdatingStatus) {
              e.target.style.background = '#5a5a5a';
            }
          }}
          onMouseLeave={(e) => {
            if (!isUpdatingStatus) {
              e.target.style.background = '#4a4a4a';
            }
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleStatusSave}
          disabled={isUpdatingStatus}
          style={{
            flex: 1,
            padding: '0.75rem 1rem',
            background: colors.primary,
            border: '1px solid ' + colors.primary,
            borderRadius: '4px',
            color: 'white',
            cursor: isUpdatingStatus ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500',
            transition: 'all 0.2s',
            opacity: isUpdatingStatus ? 0.7 : 1
          }}
          onMouseEnter={(e) => {
            if (!isUpdatingStatus) {
              e.target.style.background = colors.primaryHover;
              e.target.style.borderColor = colors.primaryHover;
            }
          }}
          onMouseLeave={(e) => {
            if (!isUpdatingStatus) {
              e.target.style.background = colors.primary;
              e.target.style.borderColor = colors.primary;
            }
          }}
        >
          {isUpdatingStatus ? 'Saving...' : 'Save Status'}
        </button>
      </div>
    </div>
  );
};

export default CaseStatusContent;
